import { z } from 'zod'
import { slugSchema } from './common.js'
import { gallerySchema } from './media.js'

export const eventStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED'])
export type EventStatus = z.infer<typeof eventStatusSchema>

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Arquivado',
}

// --- Ajuste da imagem de capa ---------------------------------------------

export const coverFitSchema = z.enum(['COVER', 'CONTAIN'])
export type CoverFit = z.infer<typeof coverFitSchema>

/** Termos escolhidos pela paroquia - evitam jargao de CSS na tela. */
export const COVER_FIT_LABELS: Record<CoverFit, string> = {
  COVER: 'Cortar',
  CONTAIN: 'Encolher para caber',
}

export const COVER_FIT_DESCRIPTIONS: Record<CoverFit, string> = {
  COVER: 'A imagem preenche todo o espaco e as sobras sao cortadas.',
  CONTAIN: 'A imagem aparece inteira, com faixas nas laterais quando necessario.',
}

export const coverPositionSchema = z.enum([
  'TOP_LEFT',
  'TOP',
  'TOP_RIGHT',
  'LEFT',
  'CENTER',
  'RIGHT',
  'BOTTOM_LEFT',
  'BOTTOM',
  'BOTTOM_RIGHT',
])
export type CoverPosition = z.infer<typeof coverPositionSchema>

export const COVER_POSITION_LABELS: Record<CoverPosition, string> = {
  TOP_LEFT: 'Canto superior esquerdo',
  TOP: 'Topo',
  TOP_RIGHT: 'Canto superior direito',
  LEFT: 'Esquerda',
  CENTER: 'Centro',
  RIGHT: 'Direita',
  BOTTOM_LEFT: 'Canto inferior esquerdo',
  BOTTOM: 'Base',
  BOTTOM_RIGHT: 'Canto inferior direito',
}

/** Grade 3x3 na ordem de leitura - monta o seletor visual de posicao. */
export const COVER_POSITION_GRID: readonly CoverPosition[] = [
  'TOP_LEFT',
  'TOP',
  'TOP_RIGHT',
  'LEFT',
  'CENTER',
  'RIGHT',
  'BOTTOM_LEFT',
  'BOTTOM',
  'BOTTOM_RIGHT',
]

const POSITION_TO_CSS: Record<CoverPosition, string> = {
  TOP_LEFT: 'left top',
  TOP: 'center top',
  TOP_RIGHT: 'right top',
  LEFT: 'left center',
  CENTER: 'center center',
  RIGHT: 'right center',
  BOTTOM_LEFT: 'left bottom',
  BOTTOM: 'center bottom',
  BOTTOM_RIGHT: 'right bottom',
}

/**
 * Traduz a escolha do coordenador em CSS.
 *
 * Centralizado aqui porque a capa aparece em cinco lugares (pagina do evento,
 * cartao da home, cartao da listagem, previa do editor e lista do painel). Com
 * a regra espalhada, a previa mostraria um recorte e o portal outro - e o
 * coordenador perderia a confianca na previa.
 *
 * `objectPosition` acompanha tambem no modo CONTAIN: quando a imagem nao
 * preenche a area, a posicao decide de que lado ficam as faixas vazias.
 */
export function coverImageStyle(
  fit: CoverFit,
  position: CoverPosition,
): { objectFit: 'cover' | 'contain'; objectPosition: string } {
  return {
    objectFit: fit === 'COVER' ? 'cover' : 'contain',
    objectPosition: POSITION_TO_CSS[position],
  }
}

export const eventInputSchema = z
  .object({
    title: z.string().min(4, 'O titulo deve ter ao menos 4 caracteres').max(200).trim(),
    slug: slugSchema.optional(),
    summary: z
      .string()
      .max(320, 'O resumo deve ter no maximo 320 caracteres')
      .trim()
      .optional(),
    description: z.string().max(200_000).optional(),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    location: z.string().min(2, 'Informe o local').max(160).trim(),
    address: z.string().max(300).trim().optional(),
    coverMediaId: z.string().nullable().optional(),
    /** Como a capa ocupa a area 16:9 do portal. Ver COVER_FIT_LABELS. */
    coverFit: coverFitSchema.default('CONTAIN'),
    /** Ancora do recorte. Sem efeito visivel quando coverFit e CONTAIN. */
    coverPosition: coverPositionSchema.default('CENTER'),
    status: eventStatusSchema.default('DRAFT'),
    featured: z.boolean().default(false),

    // --- Inscricoes (RF03.1) ---
    registrationsEnabled: z.boolean().default(false),
    registrationOpensAt: z.iso.datetime({ offset: true }).nullable().optional(),
    registrationClosesAt: z.iso.datetime({ offset: true }).nullable().optional(),
    /** Nulo = sem limite de vagas. */
    capacity: z.coerce.number().int().positive().nullable().optional(),

    /**
     * Texto livre de valores. Deliberadamente NAO e um campo monetario: o
     * RF03.1 proibe pagamento online, entao o valor aqui e informativo
     * ("Adulto R$ 30, crianca ate 10 anos gratis - pagamento na secretaria").
     */
    priceInfo: z.string().max(400).trim().optional(),

    /** RF03.2 - publica o balancete sintetico na pagina publica do evento. */
    publishBalance: z.boolean().default(false),

    /**
     * RF03.3 - publica a vitrine de patrocinadores na pagina do evento.
     *
     * Nivel de evento. O nivel de patrocinador e `showPublicly` no vinculo:
     * desligar a vitrine esconde todos, e um apoiador que pediu discricao fica
     * oculto mesmo com a vitrine ligada.
     */
    publishSponsors: z.boolean().default(false),

    /**
     * Galeria de fotos do evento.
     *
     * A ordem do array e a ordem na pagina. Util depois do evento acontecer: as
     * fotos da festa sao o que a comunidade volta para ver.
     */
    gallery: gallerySchema,
  })
  .superRefine((data, ctx) => {
    if (data.endsAt && new Date(data.endsAt) < new Date(data.startsAt)) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'O termino deve ser depois do inicio',
      })
    }
    if (
      data.registrationOpensAt &&
      data.registrationClosesAt &&
      new Date(data.registrationClosesAt) <= new Date(data.registrationOpensAt)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['registrationClosesAt'],
        message: 'O encerramento das inscricoes deve ser depois da abertura',
      })
    }
  })

export type EventInput = z.infer<typeof eventInputSchema>

export const eventListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(50).default(12),
  status: eventStatusSchema.optional(),
  /** Recorte temporal na listagem publica. */
  when: z.enum(['upcoming', 'past', 'all']).default('upcoming'),
  q: z.string().max(120).trim().optional(),
})

export type EventListQuery = z.infer<typeof eventListQuerySchema>

// --- Equipe do evento (escopo do RF07.2) -----------------------------------

export const eventMemberRoleSchema = z.enum(['COORDENADOR', 'APOIO'])
export type EventMemberRole = z.infer<typeof eventMemberRoleSchema>

export const EVENT_MEMBER_ROLE_LABELS: Record<EventMemberRole, string> = {
  COORDENADOR: 'Coordenador',
  APOIO: 'Equipe de apoio',
}

export const eventMemberInputSchema = z.object({
  userId: z.string().min(1, 'Selecione o usuario'),
  role: eventMemberRoleSchema.default('APOIO'),
})

// --- Patrocinadores (RF03.3) ----------------------------------------------

export const sponsorInputSchema = z.object({
  name: z.string().min(2, 'Informe o nome do patrocinador').max(160).trim(),
  /** Nome curto para material de divulgacao com pouco espaco. */
  shortName: z.string().max(60).trim().optional(),
  logoMediaId: z.string().nullable().optional(),
  website: z.union([z.url('Informe uma URL valida'), z.literal('')]).optional(),
  contactName: z.string().max(160).trim().optional(),
  contactPhone: z.string().max(40).trim().optional(),
  contactEmail: z.union([z.email('Informe um e-mail valido'), z.literal('')]).optional(),
  notes: z.string().max(1000).trim().optional(),
})

export type SponsorInput = z.infer<typeof sponsorInputSchema>

export const sponsorTierSchema = z.enum(['DIAMANTE', 'OURO', 'PRATA', 'BRONZE', 'APOIO'])
export type SponsorTier = z.infer<typeof sponsorTierSchema>

export const SPONSOR_TIER_LABELS: Record<SponsorTier, string> = {
  DIAMANTE: 'Diamante',
  OURO: 'Ouro',
  PRATA: 'Prata',
  BRONZE: 'Bronze',
  APOIO: 'Apoio',
}

/** Ordem canonica das cotas na vitrine publica do evento. */
export const SPONSOR_TIER_ORDER: readonly SponsorTier[] = [
  'DIAMANTE',
  'OURO',
  'PRATA',
  'BRONZE',
  'APOIO',
]

export const eventSponsorInputSchema = z.object({
  sponsorId: z.string().min(1, 'Selecione o patrocinador'),
  tier: sponsorTierSchema.default('APOIO'),
  /** Valor ou contrapartida acordada, em centavos. Interno. */
  contributionCents: z.coerce.number().int().min(0).nullable().optional(),
  /** Contrapartida nao financeira: doacao de produto, servico, estrutura. */
  inKindDescription: z.string().max(400).trim().optional(),
  sortOrder: z.coerce.number().int().min(0).default(0),
  /** Exibe na pagina publica. Alguns apoiadores pedem discricao. */
  showPublicly: z.boolean().default(true),
})

export type EventSponsorInput = z.infer<typeof eventSponsorInputSchema>

/**
 * Alteracao de um vinculo que ja existe.
 *
 * Declarado campo por campo, e NAO como
 * `eventSponsorInputSchema.omit({ sponsorId: true }).partial()`. Essa forma
 * parece equivalente e nao e: `.partial()` torna a chave opcional mas mantem o
 * `.default()`, entao um corpo `{ showPublicly: false }` chega ao handler com
 * `tier: 'APOIO'` preenchido. Ocultar um patrocinador rebaixava a cota acordada
 * de DIAMANTE para APOIO - um teste pegou isso.
 *
 * `sponsorId` fica fora porque identifica o vinculo; trocar o patrocinador de um
 * vinculo e desvincular e vincular outro.
 */
export const eventSponsorUpdateSchema = z.object({
  tier: sponsorTierSchema.optional(),
  contributionCents: z.coerce.number().int().min(0).nullable().optional(),
  inKindDescription: z.string().max(400).trim().optional(),
  sortOrder: z.coerce.number().int().min(0).optional(),
  showPublicly: z.boolean().optional(),
})

export type EventSponsorUpdate = z.infer<typeof eventSponsorUpdateSchema>
